import React, { useMemo, useState } from 'react';
import { Select, Empty } from 'antd';
import { isEmpty, startCase } from 'lodash';
import moment from 'moment';

import DualAxisAreaChart from '../../../../../../../shared/designSystem/components/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../../../../shared/globalStyling/styledText';

const { Option } = Select;

// =============================================================================
// TransactionsChartV2 — Transactions tab time-series chart.
//
// Structural mirror of CampaignOverviewChart (Display tab) so both tabs read
// the same: chart-head with Trend eyebrow + L/R axis selectors, mode-row
// with "By Date" / "By Day" segmented toggle below, chart-body with the
// shared Recharts DualAxisAreaChart. Owns its own leftY / rightY / mode
// state — the parent only supplies the filtered byDate rows.
//
// Dimensions available to the axis selectors:
//   Impressions, Transactions, Transaction Revenue
// (labels map to data keys `impressions` / `transactions` / `transactionRevenue`
// via DualAxisAreaChart's built-in labelToKey.)
// =============================================================================

const MODES = [
  { key: 'byDate', label: 'By Date' },
  { key: 'byDay',  label: 'By Day' },
];

// ISO-first (Monday=0) — same ordering the Display "By Day" chart uses.
const ORDERED_DAYS = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
  Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
};

const TransactionsChartV2 = ({ chartData }) => {
  // Default axes match develop's BizCharts implementation:
  // left = Transactions count, right = Transaction Revenue.
  const [leftY, setLeftY] = useState('Transactions');
  const [rightY, setRightY] = useState('Transaction Revenue');
  const [mode, setMode] = useState('byDate');

  // By Date — normalize xAxis to ISO so Recharts sorts cleanly.
  const byDate = useMemo(() => {
    if (isEmpty(chartData)) return [];
    return chartData
      .map((row) => ({
        ...row,
        xAxis: moment(row.xAxis, 'MM/DD/YYYY').isValid()
          ? moment(row.xAxis, 'MM/DD/YYYY').format('YYYY-MM-DD')
          : row.xAxis,
      }))
      .sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis)));
  }, [chartData]);

  // By Day — aggregate all dates by day-of-week label.
  const byDay = useMemo(() => {
    if (isEmpty(byDate)) return [];
    const buckets = {};
    byDate.forEach((row) => {
      const day = moment(row.xAxis).format('dddd');
      if (!buckets[day]) {
        buckets[day] = {
          xAxis: day,
          impressions: 0,
          transactions: 0,
          transactionRevenue: 0,
        };
      }
      buckets[day].impressions += Number(row.impressions || 0);
      buckets[day].transactions += Number(row.transactions || 0);
      buckets[day].transactionRevenue += Number(row.transactionRevenue || 0);
    });
    return Object.values(buckets).sort(
      (a, b) => (ORDERED_DAYS[a.xAxis] ?? 0) - (ORDERED_DAYS[b.xAxis] ?? 0)
    );
  }, [byDate]);

  const activeData = mode === 'byDate' ? byDate : byDay;

  // Fixed option set — the three metrics the chart can plot. Keys match
  // data fields so DualAxisAreaChart's labelToKey resolves them correctly
  // ("Transaction Revenue" → "transactionRevenue").
  const AXIS_OPTIONS = ['Impressions', 'Transactions', 'Transaction Revenue'];

  const availableModes = MODES.filter((m) => {
    if (m.key === 'byDate') return !isEmpty(byDate);
    if (m.key === 'byDay') return !isEmpty(byDay);
    return false;
  });

  if (isEmpty(activeData)) {
    return (
      <div
        className="campaign-overview__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  }

  return (
    <div className="campaign-overview__chart">
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">
          Trend
        </div>

        <div className="campaign-overview__axis-row">
          <div className="campaign-overview__axis-field">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
              Left axis
            </span>
            <Select
              onSelect={(value) => setLeftY(value)}
              className="campaign-overview__axis-select"
              value={leftY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== rightY).map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#7dafff'} />
                </Option>
              ))}
            </Select>
          </div>

          <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
              Right axis
            </span>
            <Select
              onSelect={(value) => setRightY(value)}
              className="campaign-overview__axis-select"
              value={rightY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== leftY).map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#92d5a7'} />
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {availableModes.length > 1 && (
        <div className="campaign-overview__mode-row">
          <div
            className="campaign-overview__mode segmented-tabs"
            role="tablist"
            aria-label="Chart mode"
          >
            {availableModes.map((m) => (
              <button
                key={m.key}
                role="tab"
                type="button"
                aria-selected={mode === m.key}
                onClick={() => setMode(m.key)}
                className={
                  'segmented-tabs__btn' +
                  (mode === m.key ? ' segmented-tabs__btn--active' : '')
                }
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="campaign-overview__chart-body">
        <DualAxisAreaChart
          dualChartData={activeData}
          leftY={leftY}
          rightY={rightY}
        />
      </div>
    </div>
  );
};

export default TransactionsChartV2;
